import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { netBadge } from "@/components/RecordManager";
import { BRANCHES, BRANCH_QCOUNTS } from "@/lib/constants";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Plus, Trash2, Loader2, FlaskConical, Pencil, X, Layers, ListChecks } from "lucide-react";

const NAVY = "#0B1B3F";

const HEAD_STYLE = [
  "bg-blue-600", "bg-emerald-600", "bg-amber-500", "bg-purple-600", "bg-rose-600",
  "bg-teal-600", "bg-orange-500", "bg-pink-600", "bg-indigo-600", "bg-cyan-600",
];

const EMPTY = { date: "", branch: "", name: "", publisher: "", total: "", correct: "", wrong: "", blank: "" };

export default function BranchExams({ studentId, readOnly }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkTargets, setBulkTargets] = useState({ tracks: [], students: [] });
  const [targetData, setTargetData] = useState(null);
  const EMPTY_BULK = { date: "", publisher: "", name: "", count: 10, optik: false, branches: {} };

  const toggleBulkBranch = (b) =>
    setBulk((p) => {
      const branches = { ...(p.branches || {}) };
      if (b in branches) delete branches[b];
      else branches[b] = BRANCH_QCOUNTS[b] || 40;
      return { ...p, branches };
    });

  const setBranchQ = (b, v) => setBulk((p) => ({ ...p, branches: { ...(p.branches || {}), [b]: v } }));

  const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    if (!bulk || targetData) return;
    Promise.all([api.get("/students"), api.get("/meta")])
      .then(([s, m]) => setTargetData({ students: s.data, tracks: m.data.tracks || [] }))
      .catch(() => setTargetData({ students: [], tracks: [] }));
  }, [bulk, targetData]);

  const toggleBulkTrack = (t) =>
    setBulkTargets((p) => {
      if (p.tracks.includes(t)) return { ...p, tracks: p.tracks.filter((x) => x !== t) };
      if (p.tracks.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return p;
      }
      return { ...p, tracks: [...p.tracks, t] };
    });

  const toggleBulkStudent = (id) =>
    setBulkTargets((p) => ({
      ...p,
      students: p.students.includes(id) ? p.students.filter((x) => x !== id) : [...p.students, id],
    }));

  const bulkStudentIds = () => {
    const ids = new Set([studentId, ...bulkTargets.students]);
    (targetData?.students || []).forEach((s) => {
      if (bulkTargets.tracks.includes(s.track)) ids.add(s.id);
    });
    return [...ids];
  };

  const saveBulk = async () => {
    const n = Number(bulk.count || 0);
    const brs = Object.entries(bulk.branches || {});
    if (!bulk.name.trim()) return toast.error("Deneme adı zorunlu");
    if (!brs.length) return toast.error("En az bir branş seçin");
    if (n < 1 || n > 200) return toast.error("Adet 1-200 arası olmalı");
    setBulkSaving(true);
    try {
      const base = bulk.date ? new Date(bulk.date + "T00:00:00") : null;
      const rows = [];
      for (let i = 0; i < n; i++) {
        const date = base ? fmtD(new Date(base.getTime() + i * 86400000)) : "";
        for (const [b, q] of brs) {
          const qc = Number(q) || 0;
          rows.push({ date, branch: b, name: `${bulk.name.trim()} ${i + 1}`, publisher: bulk.publisher.trim(), total: qc, correct: 0, wrong: 0, blank: qc });
        }
      }
      const { data } = await api.post(`/records-bulk/branch-exams/${studentId}`, { rows, student_ids: bulkStudentIds() });
      toast.success(`${data.created} deneme ${data.students} öğrenciye oluşturuldu (${brs.length} branş × ${n} adet) — sonuçları tabloda düzenle`);
      if (bulk.optik) {
        const ids = bulkStudentIds();
        for (let i = 0; i < n; i++) {
          const date = base ? fmtD(new Date(base.getTime() + i * 86400000)) : "";
          await api.post("/optical-exams", {
            name: `${bulk.name.trim()} ${i + 1}`, exam_type: "Branş Deneme", date,
            subjects: brs.map(([b, q]) => ({ name: b, question_count: Math.max(1, Number(q) || 1), answer_key: "", kazanimlar: [] })),
            assigned_tracks: bulkTargets.tracks, assigned_student_ids: ids,
          });
        }
        toast.success("Canlı optik sınavlar da oluşturuldu — cevap anahtarları sonra girilebilir");
      }
      setBulk(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBulkSaving(false);
    }
  };

  const uploadBulkFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBulkSaving(true);
    try {
      const text = await file.text();
      const rows = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const [date, branch, name, publisher, total, correct, wrong, blank] = l.split(";").map((x) => (x || "").trim());
          return {
            date: date || "", branch: branch || "", name,
            publisher: publisher || "",
            total: Number(total) || 0, correct: Number(correct) || 0, wrong: Number(wrong) || 0, blank: Number(blank) || 0,
          };
        })
        .filter((r) => r.name && r.branch);
      if (!rows.length) return toast.error("Dosyada geçerli satır yok");
      const { data } = await api.post(`/records-bulk/branch-exams/${studentId}`, { rows, student_ids: bulkStudentIds() });
      toast.success(`${data.created} deneme ${data.students} öğrenciye dosyadan yüklendi`);
      setBulk(null);
      load();
    } catch (e2) {
      toast.error(e2.message || apiErr(e2));
    } finally {
      setBulkSaving(false);
    }
  };

  const [resOpen, setResOpen] = useState(null);
  const [resSaving, setResSaving] = useState(false);

  const openResultEntry = () => {
    const rows = items
      .filter((r) => !Number(r.correct) && !Number(r.wrong))
      .map((r) => ({ ...r, t: "", dc: "", yc: "", bl: "" }));
    if (!rows.length) return toast.info("Sonuç bekleyen deneme yok");
    setResOpen(rows);
  };

  const saveResults = async () => {
    const updates = resOpen
      .filter((r) => r.dc !== "" || r.yc !== "" || r.bl !== "")
      .map((r) => ({
        id: r.id,
        total: Number(r.t || 0) || Number(r.dc || 0) + Number(r.yc || 0) + Number(r.bl || 0),
        correct: Number(r.dc || 0),
        wrong: Number(r.yc || 0),
        blank: Number(r.bl || 0),
      }));
    if (!updates.length) return toast.error("En az bir satır doldurun");
    setResSaving(true);
    try {
      const { data } = await api.put(`/records-bulk/branch-exams/${studentId}`, { updates });
      toast.success(`${data.updated} deneme sonucu kaydedildi — net otomatik hesaplandı`);
      setResOpen(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setResSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/branch-exams/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.branch || form.total === "" || form.correct === "" || form.wrong === "") {
      return toast.error("Tarih, branş, toplam, doğru ve yanlış zorunlu");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total: Number(form.total), correct: Number(form.correct),
        wrong: Number(form.wrong), blank: Number(form.blank || 0),
      };
      if (editId) {
        await api.put(`/branch-exams/${studentId}/${editId}`, payload);
        toast.success("Kayıt güncellendi");
        setEditId(null);
      } else {
        await api.post(`/branch-exams/${studentId}`, payload);
        toast.success("Kayıt eklendi");
      }
      setForm(EMPTY);
      load();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setForm({
      date: r.date || "", branch: r.branch || "", name: r.name || "", publisher: r.publisher || "",
      total: r.total ?? "", correct: r.correct ?? "", wrong: r.wrong ?? "", blank: r.blank ?? "",
    });
    setEditId(r.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(EMPTY);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/branch-exams/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const chartData = (() => {
    const map = {};
    [...items].sort((a, b) => (a.date || "").localeCompare(b.date || "")).forEach((i) => {
      const d = i.date || "-";
      map[d] = map[d] || { date: d };
      map[d][i.branch || "Diğer"] = Number(i.net || 0);
    });
    return Object.values(map);
  })();
  const branches = [...new Set(items.map((i) => i.branch || "Diğer"))];

  return (
    <div className="space-y-5" data-testid="branch-exams-panel">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900">Branş Deneme Sonuçları</h3>
        <p className="text-sm text-slate-500 mt-0.5">Her branşın sonuç tablosu sayfada ayrı bölüm halinde gösterilir.</p>
      </div>

      {!readOnly && (
        <form onSubmit={submit} className="card-ta p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
            <div>
              <label className="ta-label">Tarih</label>
              <input type="date" className="ta-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="field-date" />
            </div>
            <div>
              <label className="ta-label">Branş</label>
              <select className="ta-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} data-testid="field-branch">
                <option value="">Seçiniz</option>
                {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="ta-label">Deneme Adı</label>
              <input className="ta-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="field-name" />
            </div>
            <div>
              <label className="ta-label">Yayınevi</label>
              <input className="ta-input" value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })} data-testid="field-publisher" placeholder="Örn: Limit" />
            </div>
            <div>
              <label className="ta-label">Toplam Soru</label>
              <input type="number" className="ta-input" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="field-total" />
            </div>
            <div>
              <label className="ta-label">Doğru</label>
              <input type="number" className="ta-input" value={form.correct} onChange={(e) => setForm({ ...form, correct: e.target.value })} data-testid="field-correct" />
            </div>
            <div>
              <label className="ta-label">Yanlış</label>
              <input type="number" className="ta-input" value={form.wrong} onChange={(e) => setForm({ ...form, wrong: e.target.value })} data-testid="field-wrong" />
            </div>
            <div>
              <label className="ta-label">Boş</label>
              <input type="number" className="ta-input" value={form.blank} onChange={(e) => setForm({ ...form, blank: e.target.value })} data-testid="field-blank" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button type="submit" disabled={saving} className={editId ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"} data-testid="branch-add-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : editId ? <Pencil size={16} /> : <Plus size={16} />}<span className="ml-1">{editId ? "Güncelle" : "Ekle"}</span>
            </Button>
            {editId && (
              <Button type="button" variant="outline" onClick={cancelEdit} data-testid="branch-cancel-edit">
                <X size={16} /><span className="ml-1">Vazgeç</span>
              </Button>
            )}
            {!editId && (
              <Button type="button" onClick={() => { setBulk({ ...EMPTY_BULK, branches: {} }); setBulkTargets({ tracks: [], students: [] }); }} className="bg-violet-600 hover:bg-violet-700" data-testid="branch-bulk-btn">
                <Layers size={16} /><span className="ml-1">Toplu Deneme Ekle</span>
              </Button>
            )}
            {!editId && (
              <Button type="button" onClick={openResultEntry} className="bg-emerald-600 hover:bg-emerald-700" data-testid="branch-results-btn">
                <ListChecks size={16} /><span className="ml-1">Toplu Sonuç Gir</span>
              </Button>
            )}
          </div>
        </form>
      )}

      {bulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setBulk(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="branch-bulk-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Layers size={18} className="text-violet-600" /> Toplu Deneme Ekle</h3>
              <button onClick={() => setBulk(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Örn: "Acil Deneme" + Adet 10 + Türkçe & Matematik → tabloda <b>her branş için Acil Deneme 1…10</b> oluşur, sonuçları tek tek düzenlersin. Tarih girersen denemeler <b>art arda günlere</b> otomatik dağıtılır (1. deneme seçilen gün, sonrakiler ertesi günler).</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="ta-label">Deneme Adı</label>
                <input className="ta-input" value={bulk.name} onChange={(e) => setBulk({ ...bulk, name: e.target.value })} data-testid="branch-bulk-name" placeholder="Örn: Acil Deneme Deposu" />
              </div>
              <div className="col-span-2">
                <label className="ta-label">Branşlar — birden fazla seçin; soru sayısı otomatik gelir, değiştirebilirsiniz</label>
                <div className="flex flex-wrap gap-1.5">
                  {BRANCHES.map((b) => {
                    const sel = bulk.branches && b in bulk.branches;
                    return sel ? (
                      <span key={b} className="flex items-center gap-1 rounded-full border border-violet-600 bg-violet-600 text-white pl-2.5 pr-1 py-0.5">
                        <button type="button" onClick={() => toggleBulkBranch(b)} className="text-xs font-semibold" data-testid={`branch-bulk-br-${b}`}>{b} ✕</button>
                        <input type="number" min="0" value={bulk.branches[b]} onChange={(e) => setBranchQ(b, e.target.value)} className="w-12 text-xs text-center rounded bg-white text-slate-800 py-0.5" data-testid={`branch-bulk-q-${b}`} />
                      </span>
                    ) : (
                      <button key={b} type="button" onClick={() => toggleBulkBranch(b)} className="text-xs px-2.5 py-1 rounded-full border font-semibold bg-white text-slate-600 border-slate-200 hover:bg-slate-100" data-testid={`branch-bulk-br-${b}`}>{b}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={bulk.publisher} onChange={(e) => setBulk({ ...bulk, publisher: e.target.value })} data-testid="branch-bulk-publisher" placeholder="Örn: Acil Yayınları" />
              </div>
              <div>
                <label className="ta-label">Adet</label>
                <input type="number" min="1" max="200" className="ta-input" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} data-testid="branch-bulk-count" />
              </div>
              <div>
                <label className="ta-label">Başlangıç Tarihi (ops. — art arda günlere dağıtılır)</label>
                <input type="date" className="ta-input" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} data-testid="branch-bulk-date" />
              </div>
              {targetData && targetData.students.length > 0 && (
                <div className="col-span-2 border-t border-slate-100 pt-3 space-y-2" data-testid="branch-bulk-targets">
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                    <input type="checkbox" id="branch-optik" className="accent-indigo-700" checked={bulk.optik} onChange={(e) => setBulk({ ...bulk, optik: e.target.checked })} data-testid="branch-bulk-optik" />
                    <label htmlFor="branch-optik" className="text-xs text-indigo-800 font-semibold">Canlı Optik sınavı da oluştur (zorunlu değil — soru sayısı yukarıdan alınır, cevap anahtarı sonra girilebilir)</label>
                  </div>
                  <label className="ta-label">Hedef — birden fazla sınıf/alan ve öğrenci seçebilirsiniz (varsayılan: seçili öğrenci)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {targetData.tracks.map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => toggleBulkTrack(t)}
                        data-testid={`branch-bulk-track-${t}`}
                        className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                          bulkTargets.tracks.includes(t) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-28 overflow-y-auto grid grid-cols-2 gap-1 bg-white border border-slate-200 rounded-lg p-2">
                    {targetData.students
                      .filter((s) => !bulkTargets.tracks.length || bulkTargets.tracks.includes(s.track))
                      .map((s) => (
                        <label key={s.id} className="flex items-center gap-1.5 text-xs text-slate-700 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`branch-bulk-stu-${s.id}`}>
                          <input type="checkbox" className="accent-violet-600" checked={bulkTargets.students.includes(s.id)} onChange={() => toggleBulkStudent(s.id)} />
                          {s.name} {s.track ? <span className="text-slate-400">· {s.track}</span> : null}
                        </label>
                      ))}
                  </div>
                  <div className="text-[11px] text-violet-600 font-semibold">
                    {bulkStudentIds().length} öğrenciye eklenecek
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <label className="ta-label">Veya Dosya ile Toplu Yükle (.csv / .txt)</label>
              <p className="text-[11px] text-slate-400 mb-2">
                Her satır: <code className="bg-slate-50 px-1 rounded border border-slate-200 font-mono">tarih;branş;deneme_adı;yayınevi;toplam;doğru;yanlış;boş</code>
              </p>
              <input type="file" accept=".csv,.txt" onChange={uploadBulkFile} disabled={bulkSaving} className="text-xs text-slate-600" data-testid="branch-bulk-file" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulk(null)}>Vazgeç</Button>
              <Button onClick={saveBulk} disabled={bulkSaving} className="bg-violet-600 hover:bg-violet-700" data-testid="branch-bulk-save">
                {bulkSaving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Oluştur</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="card-ta p-4">
          <div className="text-sm text-slate-500 mb-3">Branş Denemesi Net Gelişimi</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={12} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8 }} />
              <Legend />
              {branches.slice(0, 5).map((b, i) => (
                <Line key={b} type="monotone" dataKey={b} stroke={["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444"][i]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : branches.length === 0 ? (
        <div className="card-ta p-10 text-center text-slate-400">Henüz branş denemesi kaydı yok.</div>
      ) : (
        branches.map((b, bi) => {
          const rows = items.filter((i) => (i.branch || "Diğer") === b).sort((a, z) => (z.date || "").localeCompare(a.date || ""));
          const tot = rows.reduce((a, r) => a + Number(r.total || 0), 0);
          const cor = rows.reduce((a, r) => a + Number(r.correct || 0), 0);
          const wr = rows.reduce((a, r) => a + Number(r.wrong || 0), 0);
          const bl = rows.reduce((a, r) => a + Number(r.blank || 0), 0);
          return (
            <div key={b} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid={`branch-table-${b}`}>
              <div className={`${HEAD_STYLE[bi % HEAD_STYLE.length]} text-white px-4 py-2.5 flex items-center justify-between`}>
                <span className="font-display font-bold tracking-wide flex items-center gap-2"><FlaskConical size={15} /> {b.toUpperCase()}</span>
                <span className="text-xs font-semibold opacity-90">{rows.length} deneme · Net {rows.reduce((a, r) => a + Number(r.net || 0), 0).toFixed(2)}</span>
              </div>
              <div className="overflow-x-auto bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                      <th className="px-4 py-2.5 font-medium">Tarih</th>
                      <th className="px-4 py-2.5 font-medium">Deneme</th>
                      <th className="px-4 py-2.5 font-medium">Yayın</th>
                      <th className="px-4 py-2.5 font-medium text-right">Toplam</th>
                      <th className="px-4 py-2.5 font-medium text-right">Doğru</th>
                      <th className="px-4 py-2.5 font-medium text-right">Yanlış</th>
                      <th className="px-4 py-2.5 font-medium text-right">Boş</th>
                      <th className="px-4 py-2.5 font-medium text-right">Net</th>
                      {!readOnly && <th className="px-4 py-2.5" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2.5">{r.date || "-"}</td>
                        <td className="px-4 py-2.5 text-slate-600">{r.name || "-"}</td>
                        <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600">{r.publisher || "-"}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{r.total}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{r.correct}</td>
                        <td className="px-4 py-2.5 text-right text-rose-700 font-semibold">{r.wrong}</td>
                        <td className="px-4 py-2.5 text-right text-slate-500">{r.blank ?? 0}</td>
                        <td className="px-4 py-2.5 text-right">{netBadge(r.net)}</td>
                        {!readOnly && (
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            <button onClick={() => startEdit(r)} className="text-slate-300 hover:text-amber-600 mr-3" data-testid={`branch-edit-${r.id}`}><Pencil size={14} /></button>
                            <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-rose-600" data-testid={`branch-del-${r.id}`}><Trash2 size={14} /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-semibold">
                      <td className="px-4 py-2.5" colSpan={3}>Toplam</td>
                      <td className="px-4 py-2.5 text-right">{tot}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700">{cor}</td>
                      <td className="px-4 py-2.5 text-right text-rose-700">{wr}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{bl}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-indigo-700">{(cor - wr / 4).toFixed(2)}</td>
                      {!readOnly && <td />}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {resOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setResOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="branch-results-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><ListChecks size={18} className="text-emerald-600" /> Toplu Sonuç Gir (elle)</h3>
              <button onClick={() => setResOpen(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Sonuç bekleyen denemeler listelenir. Toplam boş bırakılırsa D+Y+B toplamı alınır; net otomatik hesaplanır ve tüm panellerde görünür.</p>
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 text-[11px] font-bold text-slate-400 uppercase px-1">
                <span>Deneme</span><span className="text-center">Toplam</span><span className="text-center">D</span><span className="text-center">Y</span><span className="text-center">Boş</span>
              </div>
              {resOpen.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 items-center bg-slate-50 rounded-lg px-2 py-1.5">
                  <div className="text-xs font-semibold text-slate-700 truncate">{r.name}<span className="text-slate-400 font-normal"> · {r.date || "-"}</span></div>
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.t} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, t: e.target.value } : x)))} data-testid={`res-t-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center border-emerald-200" value={r.dc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, dc: e.target.value } : x)))} data-testid={`res-d-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center border-rose-200" value={r.yc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, yc: e.target.value } : x)))} data-testid={`res-y-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.bl} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, bl: e.target.value } : x)))} data-testid={`res-b-${r.id}`} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResOpen(null)}>Vazgeç</Button>
              <Button onClick={saveResults} disabled={resSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="branch-results-save">
                {resSaving ? <Loader2 className="animate-spin" size={15} /> : <ListChecks size={15} />}<span className="ml-1">Sonuçları Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
